import { useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { gu } from '../utils/conversions';
import { sn, fmt$ } from '../utils/formatters';
import { logActivity } from '../lib/activityLog';
import IngredientForm from './IngredientForm';
import Modal from './Modal';

export default function ReplaceIngredient({ig,t,si,sb,mn,orgId,uid,ings,setIngs,batches,mi,setMi,setBatches,save,settings,onClose,setPg,setSel,setReturnTo}){
const[qtyAdj,setQtyAdj]=useState({});
const[saving,setSaving]=useState(false);

// Find all recipes using this ingredient
const usedIn=useMemo(()=>{
  const refs=[];
  batches.forEach(b=>{
    b.r.forEach((line,i)=>{
      if(line.i===ig.id)refs.push({type:'batch',id:b.id,name:b.n,idx:i,qty:line.q,unit:line.u});
    });
  });
  mi.forEach(m=>{
    m.r.forEach((line,i)=>{
      if(line.i===ig.id)refs.push({type:'menu',id:m.id,name:m.n,idx:i,qty:line.q,unit:line.u});
    });
  });
  return refs;
},[ig.id,batches,mi]);

const oldUnitCost=gu(ig,ig.cu);

function getAdjQty(ref){const k=ref.type+'_'+ref.id+'_'+ref.idx;return qtyAdj[k]!==undefined?parseFloat(qtyAdj[k]):ref.qty;}
function setAdj(ref,v){const k=ref.type+'_'+ref.id+'_'+ref.idx;setQtyAdj(p=>({...p,[k]:v}));}

async function handleReplace(formData){
  setSaving(true);
  const newId=`i${Date.now()}`;
  const newIng={id:newId,...formData,tier:ig.tier,loc:ig.loc,par:ig.par,pk:ig.pk,so:ig.so,active:true};

  // Create new ingredient
  save.insertIngredient(orgId,uid,newIng);

  // Update all recipe references
  for(const ref of usedIn){
    const adjQty=getAdjQty(ref);
    if(ref.type==='batch'){
      await supabase.from('batch_ingredients').update({ingredient_id:newId,qty:adjQty}).eq('batch_id',ref.id).eq('ingredient_id',ig.id);
    }else{
      await supabase.from('recipe_ingredients').update({ingredient_id:newId,qty:adjQty}).eq('recipe_id','r_'+ref.id).eq('ingredient_id',ig.id);
      await supabase.from('menu_item_ingredients').update({ingredient_id:newId,qty:adjQty}).eq('menu_item_id',ref.id).eq('ingredient_id',ig.id);
    }
  }

  // Deactivate old ingredient
  save.deactivateIngredient(ig.id);

  // Copy price history for continuity
  const{data:history}=await supabase.from('price_history').select('*').eq('ingredient_id',ig.id);
  if(history?.length){
    const rows=history.map(h=>({user_id:h.user_id,org_id:h.org_id,ingredient_id:newId,old_price:h.old_price,new_price:h.new_price,source:h.source}));
    await supabase.from('price_history').insert(rows);
  }
  // Log the price transition
  if(Math.abs(ig.cp-formData.cp)>0.01){
    await supabase.from('price_history').insert({user_id:uid,org_id:orgId,ingredient_id:newId,old_price:ig.cp,new_price:formData.cp,source:'manual'});
  }

  // Activity log
  logActivity(orgId,'price','ingredient_replaced',`Replaced ${sn(ig.n)} with ${formData.n} in ${usedIn.length} recipe${usedIn.length!==1?'s':''}`,{oldId:ig.id,newId,oldName:ig.n,newName:formData.n,recipeCount:usedIn.length});

  // Update local state
  setIngs(p=>[...p.map(i=>i.id===ig.id?{...i,active:false}:i),newIng]);
  setBatches(p=>p.map(b=>({...b,r:b.r.map(l=>l.i===ig.id?{...l,i:newId,q:getAdjQty(usedIn.find(r=>r.type==='batch'&&r.id===b.id)||l)}:l)})));
  setMi(p=>p.map(m=>({...m,r:m.r.map(l=>l.i===ig.id?{...l,i:newId,q:getAdjQty(usedIn.find(r=>r.type==='menu'&&r.id===m.id)||l)}:l)})));

  setSaving(false);
  onClose();
}

return(
<Modal open={true} onClose={onClose} width={680} t={t}>

<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
<div><div style={{fontSize:16,fontWeight:700}}>Replace Ingredient</div><div style={{fontSize:12,color:t.td,marginTop:2}}>Replacing: <span style={{fontWeight:600,color:t.ac}}>{ig.n}</span> — {ig.b}</div></div>
<button onClick={onClose} style={{background:"none",border:"none",color:t.tm,fontSize:18,cursor:"pointer",padding:4}}>✕</button>
</div>

<IngredientForm mode="replace" initial={null} onSave={data=>{if(!saving)handleReplace(data);}} onCancel={onClose} categories={[...new Set(ings.map(i=>i.cat))].sort()} vendors={settings.vendors.sort()} t={t} si={si} sb={sb} mn={mn}/>

{/* Affected recipes */}
{usedIn.length>0&&<div style={{marginBottom:16}}>
<div style={{fontSize:12,fontWeight:600,marginBottom:8}}>Affected Recipes ({usedIn.length})</div>
<div style={{border:`1px solid ${t.bd}`,borderRadius:8,overflow:"hidden"}}>
<table style={{width:"100%",borderCollapse:"collapse"}}>
<thead><tr style={{background:t.sf}}><th style={{padding:"8px 10px",fontSize:9,fontWeight:600,color:t.td,textAlign:"left",textTransform:"uppercase"}}>Recipe</th><th style={{padding:"8px 10px",fontSize:9,fontWeight:600,color:t.td,textAlign:"left",textTransform:"uppercase"}}>Type</th><th style={{padding:"8px 10px",fontSize:9,fontWeight:600,color:t.td,textAlign:"center",textTransform:"uppercase"}}>Current Qty</th><th style={{padding:"8px 10px",fontSize:9,fontWeight:600,color:t.td,textAlign:"center",textTransform:"uppercase"}}>New Qty</th><th style={{padding:"8px 10px",fontSize:9,fontWeight:600,color:t.td,textAlign:"right",textTransform:"uppercase"}}>Current Cost</th></tr></thead>
<tbody>{usedIn.map((ref,i)=>{
  const adjQty=getAdjQty(ref);
  const oldCost=oldUnitCost*ref.qty;
  return(
  <tr key={i} style={{borderBottom:`1px solid ${t.bd}`}}>
    <td style={{padding:"8px 10px",fontSize:12,fontWeight:500}}>{setPg?<span onClick={()=>{if(setReturnTo)setReturnTo({fromPage:'pricesheet',fromId:ig.id,fromName:ig.n,mode:'replace'});onClose();setPg(ref.type==='batch'?'batches':'costcards');setTimeout(()=>setSel(ref.id),50);}} style={{cursor:"pointer",color:ref.type==='batch'?t.bl:t.yl,textDecoration:"underline"}}>{ref.name}</span>:ref.name}</td>
    <td style={{padding:"8px 10px"}}><span style={{fontSize:9,padding:"2px 5px",borderRadius:3,background:ref.type==='batch'?"rgba(59,130,246,0.1)":"rgba(212,168,36,0.1)",color:ref.type==='batch'?t.bl:t.yl}}>{ref.type}</span></td>
    <td style={{padding:"8px 10px",fontFamily:mn,fontSize:11,textAlign:"center"}}>{ref.qty} {ref.unit}</td>
    <td style={{padding:"8px 10px",textAlign:"center"}}><input type="number" value={qtyAdj[ref.type+'_'+ref.id+'_'+ref.idx]??ref.qty} onChange={e=>setAdj(ref,e.target.value)} style={{...si,width:65,padding:"3px 6px",fontFamily:mn,fontSize:11,textAlign:"center"}} step="any"/></td>
    <td style={{padding:"8px 10px",fontFamily:mn,fontSize:11,textAlign:"right"}}>${fmt$(oldCost)}</td>
  </tr>);})}</tbody></table>
</div>
</div>}

{usedIn.length===0&&<div style={{background:t.sf,borderRadius:8,padding:16,marginBottom:16,textAlign:"center",color:t.td,fontSize:12}}>This ingredient is not used in any recipes.</div>}

<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderTop:`1px solid ${t.bd}`}}>
<div style={{fontSize:11,color:t.td}}>{usedIn.length} recipe{usedIn.length!==1?'s':''} will be updated. The old ingredient will be archived.</div>
<div style={{fontFamily:mn,fontSize:12}}><span style={{color:t.td}}>Old unit cost: </span><span style={{fontWeight:600}}>${oldUnitCost.toFixed(4)}/{ig.cu.toLowerCase()}</span></div>
</div>

</Modal>);
}
